import { z } from "zod";
import { OCRDocumentIdSchema } from "../Document/schemas";
import { ClientId, DateSchema, PaginationMetadata, PortfolioPlanId, Ticker, VirtualAccountId, } from "../Types";
export const TransitionProposalIdSchema = z
    .string()
    .startsWith("TNP_")
    .regex(/^TNP_[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/)
    .brand("TransitionProposalId");
export const OCRAccountHoldingIdSchema = z
    .string()
    .startsWith("OCRAH_")
    .regex(/^OCRAH_[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/)
    .brand("OCRAccountHoldingId");
export const TransitionProposalStatusSchema = z
    .enum(["draft", "processing", "processed", "complete", "shared"])
    .default("draft");
export const TransitionProposalImportTypeSchema = z.enum([
    "existing",
    "manual",
    "statement",
]);
export const TransitionProposalSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    clientId: ClientId,
    name: z.string(),
    importType: TransitionProposalImportTypeSchema,
    status: TransitionProposalStatusSchema,
    isRead: z.boolean(),
    proposedPortfolioPlanId: PortfolioPlanId.nullish(),
    proposedPortfolioPlanVersion: z.number().nullish(),
    createdAt: DateSchema,
    updatedAt: DateSchema,
});
// -- Create Transition Proposal Schemas --
export const CreateProposalRequestBodySchema = z.object({
    clientId: ClientId,
    name: z.string(),
    importType: TransitionProposalImportTypeSchema,
});
export const CreateSuccessResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
// -- Update Transition Proposal Schemas --
export const UpdateProposalParamsSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
});
export const UpdateProposalRequestBodySchema = z.object({
    name: z.string().optional(),
    status: TransitionProposalStatusSchema.optional(),
    importType: TransitionProposalImportTypeSchema.optional(),
    proposedPortfolioPlanId: PortfolioPlanId.nullish(),
    proposedPortfolioPlanVersion: z.number().nullish(),
    isRead: z.boolean().optional(),
});
export const UpdateSuccessResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
// -- Delete Transition Proposal Schemas --
export const DeleteProposalResponseSchema = z.object({});
// -- Pagination Schemas --
export const pageQueryString = z
    .string()
    .default("1")
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= 1, { message: "Must be greater than or equal to 1" })
    .describe("The page number, default is 1");
export const defaultPageSize = 10;
export const minPageSize = 1;
export const maxPageSize = 100;
export const pageSizeQueryString = z
    .string()
    .default(defaultPageSize.toString())
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= minPageSize, {
    message: `Must be greater than or equal to ${minPageSize}`,
})
    .refine((v) => v <= maxPageSize, {
    message: `Must be less than or equal to ${maxPageSize}`,
})
    .describe(`The number of items per page, default is ${defaultPageSize}. Must be between ${minPageSize} and ${maxPageSize}`);
export const AllowedHoldingOrderFieldsSchema = z
    .enum([
    "name",
    "ticker",
    "quantity",
    "costBasis",
    "price",
    "marketValue",
    "unrealizedGainOrLoss",
    "percentOfAccount",
    "statementPeriod",
    "type",
    "fileName",
    "accountName",
])
    .default("marketValue");
export const AllowedSortOrderSchema = z
    .enum(["asc", "desc", "ASC", "DESC"])
    .default("desc");
export const ProposalOrderBySchema = z
    .enum(["createdAt asc", "createdAt desc"])
    .default("createdAt desc");
export const ProposalHoldingsOrderBySchema = z
    .string()
    .superRefine((val, ctx) => {
    const parts = val.split(" ");
    if (parts.length !== 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid orderBy format. Must be 'field direction' (e.g., 'marketValue desc').",
        });
        return;
    }
    const [field, direction] = parts;
    const isFieldValid = AllowedHoldingOrderFieldsSchema.safeParse(field).success;
    const isDirectionValid = AllowedSortOrderSchema.safeParse(direction).success;
    const allowedFields = Object.values(AllowedHoldingOrderFieldsSchema._def.innerType.Values);
    const allowedDirections = Object.values(AllowedSortOrderSchema._def.innerType.Values);
    if (!isFieldValid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid field: '${field}'. Allowed fields: ${allowedFields.join(", ")}`,
        });
    }
    if (!isDirectionValid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid sort direction: '${direction}'. Allowed directions: ${allowedDirections.join(", ")}`,
        });
    }
});
// -- Get Transition Proposals Schemas --
export const GetTransitionProposalResponseBodySchema = z.object({
    data: z.array(TransitionProposalSchema),
    pagination: PaginationMetadata,
});
// -- Get Single Transition Proposal Schemas --
export const GetSingleTransitionProposalResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
// -- Get Unread Transition Proposals Count Schemas --
export const GetUnreadTransitionProposalsResponseBodySchema = z.object({
    data: z.object({ unreadCount: z.number() }),
});
export const GetProposalsQuerySchema = z.object({
    page: pageQueryString.optional(),
    pageSize: pageSizeQueryString.optional(),
    orderBy: ProposalOrderBySchema.optional(),
    status: TransitionProposalStatusSchema.optional(),
    clientId: ClientId,
});
// -- Duplicate Transition Proposal Schemas --
export const DuplicateProposalRequestBodySchema = z.object({});
export const DuplicateSuccessResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
export const DuplicateProposalParamsSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
});
// -- Share Transition Proposal Schemas --
export const ShareTransitionProposalRequestBodySchema = z.object({});
export const ShareTransitionProposalResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
export const ShareSuccessResponseBodySchema = z.object({
    data: TransitionProposalSchema,
});
// -- Associate Transition Proposal with OCR Document Schemas --
export const AssociateProposalRequestBodySchema = z.object({
    ocrDocumentIds: z.array(OCRDocumentIdSchema).min(1),
});
export const AssociateSuccessResponseBodySchema = z.object({
    data: z.object({}),
});
// -- Get Transition Proposal Status Schemas --
export const StatusResponseBodySchema = z.object({
    data: TransitionProposalIdSchema.nullable(),
});
// -- Get Transition Proposal Holdings Schemas --
export const TransitionProposalHoldingTableFieldsSchema = z.object({
    name: z.string().nullable(),
    ticker: Ticker.nullable(),
    quantity: z.string().nullable(),
    costBasis: z.string().nullable(),
    price: z.string().nullable(),
    marketValue: z.string().nullable(),
    unrealizedGainOrLoss: z.string().nullable(),
    percentOfAccount: z.string().nullable(),
    type: z.string().nullable(),
    fileName: z.string().nullable(),
    accountName: z.string().nullable(),
    statementPeriod: z.string().nullable(),
});
export const TransitionProposalHoldingsFilterSchema = z.object({
    name: z.string().optional(),
    ticker: z.string().optional(),
    quantity: z.string().optional(),
    costBasis: z.string().optional(),
    price: z.string().optional(),
    marketValue: z.string().optional(),
    unrealizedGainOrLoss: z.string().optional(),
    percentOfAccount: z.string().optional(),
    statementPeriod: z.string().optional(),
    type: z.string().optional(),
    fileName: z.string().optional(),
    accountName: z.string().optional(),
});
const numericString = z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, { message: "Must be a valid number string" })
    .nullable();
export const NumericStringFilterTypes = [
    "price",
    "costBasis",
    "quantity",
    "marketValue",
    "unrealizedGainOrLoss",
    "percentOfAccount",
];
export const NumericStringFilterTypesSchema = z.enum(NumericStringFilterTypes);
export const TextStringFilterTypes = [
    "name",
    "ticker",
    "statementPeriod",
    "type",
    "fileName",
    "accountName",
];
export const TextStringFilterTypesSchema = z.enum(TextStringFilterTypes);
export const TransitionProposalHoldingsFilterResponseSchema = z.object({
    name: z.string().array(),
    quantity: z.tuple([numericString, numericString]).default([null, null]),
    costBasis: z.tuple([numericString, numericString]).default([null, null]),
    price: z.tuple([numericString, numericString]).default([null, null]),
    marketValue: z.tuple([numericString, numericString]).default([null, null]),
    unrealizedGainOrLoss: z
        .tuple([numericString, numericString])
        .default([null, null]),
    percentOfAccount: z
        .tuple([numericString, numericString])
        .default([null, null]),
    statementPeriod: z.string().array(),
    type: z.string().array(),
    fileName: z.string().array(),
    accountName: z.string().array(),
    ticker: z.string().array(),
});
export const GetProposalHoldingsQuerySchema = z
    .object({
    page: pageQueryString.optional(),
    pageSize: pageSizeQueryString.optional(),
    orderBy: ProposalHoldingsOrderBySchema.optional(),
})
    .merge(TransitionProposalHoldingsFilterSchema);
export const AccountHoldingFieldValueSchema = z.object({
    accountHoldingId: OCRAccountHoldingIdSchema,
    fieldName: TransitionProposalHoldingTableFieldsSchema,
    value: z.string().nullable(),
});
export const TransitionProposalTableHoldingSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    accountHoldingId: OCRAccountHoldingIdSchema,
    fields: TransitionProposalHoldingTableFieldsSchema,
});
export const TransitionProposalHoldingsResponseBodySchema = z.object({
    data: z.array(TransitionProposalTableHoldingSchema),
    pagination: PaginationMetadata,
    filters: TransitionProposalHoldingsFilterResponseSchema,
});
// -- Create Transition Proposal Holding Schemas --
export const CreateHoldingRequestBodySchema = z.object({
    name: z.string().nullable(),
    ticker: Ticker.nullable(),
    quantity: z.string().nullable(),
    costBasis: z.string().nullable(),
    price: z.string().nullable().nullable(),
    marketValue: z.string().nullable(),
    unrealizedGainOrLoss: z.string().nullable(),
    percentOfAccount: z.string().nullable(),
    type: z.string().nullable(),
    fileName: z.string().nullable(),
    accountName: z.string().nullable(),
    statementPeriod: z.string().nullable(),
});
export const CreateHoldingSuccessResponseBodySchema = z.object({
    data: TransitionProposalTableHoldingSchema,
});
// -- Update Transition Proposal Holding Schemas --
export const UpdateHoldingParamsSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    accountHoldingId: OCRAccountHoldingIdSchema,
});
export const UpdateHoldingRequestBodySchema = z.object({
    fields: TransitionProposalHoldingTableFieldsSchema,
});
export const UpdateHoldingSuccessResponseBodySchema = z.object({
    data: TransitionProposalTableHoldingSchema,
});
// -- Delete Transition Proposal Holding Schemas --
export const DeleteHoldingParamsSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    accountHoldingId: OCRAccountHoldingIdSchema,
});
// -- Add exisitng account holdings to transition proposal --
export const AddExistingAccountHoldingsRequestBodySchema = z.object({
    virtualAccountIds: z.array(VirtualAccountId),
});
export const AddExistingAccountHoldingsSuccessResponseSchema = z.object({
    data: z.object({}),
});
export const DeleteHoldingResponseSchema = z.object({});
// -- Get Transition Proposal Summary Schemas --
export const OCRAnalysisSummarySchema = z.object({
    marketValue: z.string(),
    unrealizedGainOrLoss: z.string(),
    summary: z.string(),
});
export const TransitionProposalAnalysisSummarySchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    fields: OCRAnalysisSummarySchema,
});
export const GetTransitionProposalAnalysisSummaryResponseBodySchema = z.object({
    data: TransitionProposalAnalysisSummarySchema,
});
export const TypeBreakdownSchema = z.object({
    type: z.string(),
    weight: z.number(),
});
export const HoldingBreakdownSchema = z.object({
    ticker: z.string().nullable(),
    cusip: z.string().nullable(),
    weight: z.number(),
});
export const RejectedHoldingSchema = z.object({
    ticker: z.string().nullable(),
    cusip: z.string().nullable(),
    name: z.string().nullable(),
    message: z.array(z.string()),
});
export const TransitionProposalBreakdownSchema = z.object({
    typesBreakdown: z.array(TypeBreakdownSchema),
    holdingsBreakdown: z.array(HoldingBreakdownSchema),
    rejected: z.array(RejectedHoldingSchema),
});
export const GetHoldingsBreakdownSuccessResponseBodySchema = z.object({
    data: TransitionProposalBreakdownSchema,
});
export const TransitionProposalPerformanceMetricEnumSchema = z.enum([
    "returnTotalHist1yr",
    "returnTotalHist5yr",
    "returnTotalHist10yr",
    "yield1yr",
    "finscore",
    "volatilityHistMonthly1yr",
    "volatilityHistMonthly5yr",
    "volatilityHistMonthly10yr",
]);
export const TransitionProposalPortfolioViewEnumSchema = z.enum([
    "current",
    "proposed",
]);
export const TransitionProposalPerformanceSchema = z.object({
    transitionProposalId: TransitionProposalIdSchema,
    current: z.record(TransitionProposalPerformanceMetricEnumSchema, z.boolean()),
    proposed: z.record(TransitionProposalPerformanceMetricEnumSchema, z.boolean()),
});
export const TransitionProposalPerformanceResponseSchema = z.object({
    data: TransitionProposalPerformanceSchema,
});
export const UpdatePerformanceVisibilityRequestBodySchema = z.object({
    portfolioView: TransitionProposalPortfolioViewEnumSchema,
    metrics: TransitionProposalPerformanceMetricEnumSchema.array(),
    visible: z.boolean(),
});
//# sourceMappingURL=schemas.js.map